import PusherPlugin from '@/plugins/PusherPlugin'
import { useUserStore } from '@/stores/userStore'
import { useEventStore } from '@/stores/eventStore'
import { useFetch } from '@/composables/useFetch'
import { dateSpanishFormatter } from '@/helpers/Date'
import { notificationErrorImportingPeople } from '@/components/notifications/NotificationErrorImportingPeople'
import { notificationImportPeopleFinished } from '@/components/notifications/NotificationImportPeopleFinished'
import { notificationImportPreregisterEmailsFinished } from '@/components/notifications/NotificationImportPreregisterEmailsFinished'

export const useNotificationStore = defineStore('notificationStore', () => {
  const userStore = useUserStore()
  const eventStore = useEventStore()
  const notificationsIndex = ref(JSON.parse(localStorage.getItem('notificationIndex')) ?? [])
  const opened = ref(false)
  const toast = ref([])
  const notificationWarning = ref(false)
  const filterByEvent = ref(localStorage.getItem('notification.filterByEvent') == 'true')
  const pusherChannelName = userStore.userHasRole('company_administrator') ? 'company' : 'backoffice'
  const channelName = `${pusherChannelName}-${userStore.getId()}`

  watch(filterByEvent, () => {
    localStorage.setItem('notification.filterByEvent', filterByEvent.value)
    getNotifications()
  })

  function subscribe() {
    const channel = PusherPlugin.pusher.subscribe(channelName)
    channel.bind('notification-success', event => receiveNotification('success', event.data))
    channel.bind('Export Job Finished', event => {
      receiveNotification(event.data.status, event.data)
    })
    channel.bind('Import Job Finished', event => {
      receiveNotification(event.data.status, event.data)
    })
    channel.bind('notification-error', event => receiveNotification('error', event.data))
    channel.bind('Error importing people', event => notificationErrorImportingPeople(event.data))
    channel.bind('Import people finished', event => notificationImportPeopleFinished(event.data))
    channel.bind('Import pre-register emails finished', event =>
      notificationImportPreregisterEmailsFinished(event.data)
    )
    channel.bind('export finished successfully', event =>
      receiveNotification('export finished successfully', event.data)
    )
    channel.bind('Create Invoice Job Finished', event => receiveNotification(event.data.status, event.data))
    channel.bind('Proforma Invoice Job Finished', event => receiveNotification(event.data.status, event.data))
    channel.bind('Manually Invoice Job Finished', event => receiveNotification(event.data.status, event.data))
    channel.bind('Create Payment Receipt Job Finished', event => receiveNotification(event.data.status, event.data))
    channel.bind('Massive Assign Finished', event => receiveNotification(event.data.status, event.data))
  }

  function receiveNotification(type, data) {
    const id = createToast(type, data)
    addNotificationWarning()
    setTimeout(() => {
      removeFromToastArray(id)
    }, 8000)
  }

  function addNotificationWarning() {
    sessionStorage.setItem('notificationWarning', true)
    notificationWarning.value = true
  }

  function removeNotificationWarning() {
    sessionStorage.setItem('notificationWarning', false)
    notificationWarning.value = false
  }

  function shouldShowNotificationWarning() {
    return sessionStorage.getItem('notificationWarning') == 'true' || notificationWarning.value
  }

  function updateStatus(status = null) {
    if (opened.value) {
      for (let index in notificationsIndex.value) {
        notificationsIndex.value[index].status = status ?? 'readed'
      }
      localStorage.setItem('notificationIndex', JSON.stringify(notificationsIndex.value))
    }
    opened.value = !opened.value
  }

  function getRequestUrl(urlPage) {
    if (urlPage) {
      return urlPage
    } else if (unref(filterByEvent) === true && eventStore.getId()) {
      return `/api/v1/events/${eventStore.getId()}/pusher-notifications/channel/${channelName}/only-checked/false`
    } else {
      return `/api/v1/pusher-notifications/channel/${channelName}/only-checked/false`
    }
  }

  async function getNotifications(urlPage = null) {
    const requestUrl = getRequestUrl(urlPage)

    const { data, success, promise } = useFetch(requestUrl)
    await promise
    if (unref(success)) {
      saveNotificationsInCache(unref(data).data)
    }
  }

  function saveNotificationsInCache(data) {
    notificationsIndex.value = data
    localStorage.setItem('notificationsIndex', JSON.stringify(data))
  }

  async function markNotificationAsRead(notificationId) {
    const index = notificationsIndex.value.data.findIndex(element => element.id === notificationId)
    if (index > 1) {
      const eventId = notificationsIndex.value.data
      console.log(eventId)
    }
    const url = `/api/v1/events/${eventStore.getId()}/pusher-notifications/${notificationId}/channel/${pusherChannelName}-${userStore.getId()}`
    const { data, success, promise } = useFetch(url, {}, 'PUT')
    await promise
  }

  async function deleteNotification(notificationId) {
    const url = `/api/v1/events/${eventStore.getId()}/pusher-notifications/${notificationId}/channel/${pusherChannelName}-${userStore.getId()}`
    const { data, success, promise } = useFetch(url, {}, 'DELETE')
    await promise
    const index = notificationsIndex.value.data.findIndex(element => element.id === notificationId)
    if (index > -1) {
      notificationsIndex.value.data.splice(index, 1)
    }
  }

  function createToast(type, data) {
    const now = new Date()
    const timestamp = now.getTime()

    toast.value.unshift({
      id: timestamp,
      type: type,
      message: data,
      time: dateSpanishFormatter(new Date(), true)
    })
    return timestamp
  }

  function removeFromToastArray(id) {
    const index = toast.value.findIndex(element => element.id == id)
    if (index > -1) toast.value.splice(index, 1)
  }

  function getToasts() {
    return toast.value
  }

  function getUrl(string) {
    const urlRegex = /(https?:\/\/[^\s]+)/g
    if (string.url) string = string.url
    const urls = string.match(urlRegex)

    if (urls) {
      const extractedUrls = urls.map(url => url.trim())
      return extractedUrls
    }
  }

  function getText(string, type = null) {
    if (type == 'export finished successfully') {
      return 'The export of persons is ready'
    }
    const urlRegex = /(https?:\/\/[^\s]+)/g
    if (string.url) string = string.url
    const parts = string.split(urlRegex)
    const nonUrls = parts.filter(part => !part.match(urlRegex))
    return nonUrls.join('')
  }

  return {
    subscribe,
    shouldShowNotificationWarning,
    removeNotificationWarning,
    updateStatus,
    getToasts,
    getText,
    getUrl,
    filterByEvent,
    getNotifications,
    notificationsIndex,
    markNotificationAsRead,
    deleteNotification
  }
})
